import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import {
  Film,
  Plus,
  Trash2,
  ExternalLink,
  Loader2,
  AlertTriangle,
  Upload,
  FileText,
  Download,
  ShieldCheck,
  ShieldAlert,
  RefreshCw,
  Check,
  Ban,
  Flag,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Extract a YouTube video ID from any common URL shape:
 *   https://www.youtube.com/watch?v=abc123
 *   https://youtu.be/abc123
 *   https://www.youtube.com/embed/abc123
 *   https://www.youtube.com/shorts/abc123
 * Returns null if no ID can be parsed.
 */
const extractYouTubeId = (raw) => {
  if (!raw) return null;
  const s = raw.trim();
  // Bare ID (11 chars, alnum + - _)
  if (/^[A-Za-z0-9_-]{8,15}$/.test(s)) return s;
  try {
    const u = new URL(s);
    if (u.searchParams.get('v')) return u.searchParams.get('v');
    const path = u.pathname.replace(/^\//, '');
    const parts = path.split('/');
    // youtu.be/<id>
    if (u.hostname.includes('youtu.be')) return parts[0] || null;
    // /embed/<id>  or /shorts/<id>  or /live/<id>
    if (['embed', 'shorts', 'live', 'v'].includes(parts[0])) {
      return parts[1] || null;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Parse a CSV-or-TSV string into import rows. Supports:
 *   topic,url,title,channel,duration    (header optional; detected if first
 *                                        cell equals "topic" ignoring case)
 *   # comments      — lines starting with # are skipped
 *   quoted fields   — "Tim Keller, Jr." works
 *
 * Returns { rows: [{topic, youtube_id, title, channel, duration_label,
 * _error}], errorCount }.
 */
const parseCsv = (text) => {
  if (!text) return { rows: [], errorCount: 0 };
  // Auto-detect tab vs comma
  const firstLine = text.split(/\r?\n/).find((l) => l.trim() && !l.trim().startsWith('#')) || '';
  const delim = firstLine.includes('\t') ? '\t' : ',';

  // Tiny CSV tokenizer — handles quoted fields containing the delimiter.
  const splitLine = (line) => {
    const out = [];
    let cur = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQ) {
        if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; continue; }
        if (c === '"') { inQ = false; continue; }
        cur += c;
      } else {
        if (c === '"') { inQ = true; continue; }
        if (c === delim) { out.push(cur); cur = ''; continue; }
        cur += c;
      }
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };

  const rawLines = text.split(/\r?\n/);
  const cleaned = rawLines
    .map((l, i) => ({ i, l: l.trim() }))
    .filter((x) => x.l && !x.l.startsWith('#'));
  if (!cleaned.length) return { rows: [], errorCount: 0 };

  // Detect header
  const firstCells = splitLine(cleaned[0].l);
  const hasHeader = (firstCells[0] || '').toLowerCase() === 'topic';
  const dataLines = hasHeader ? cleaned.slice(1) : cleaned;

  let errorCount = 0;
  const rows = dataLines.map(({ l }) => {
    const cells = splitLine(l);
    const [topicRaw, urlRaw, titleRaw, channelRaw, durationRaw] = cells;
    const topic = (topicRaw || '').toLowerCase();
    const youtube_id = extractYouTubeId(urlRaw || '');
    const errors = [];
    if (!topic) errors.push('missing topic');
    if (!youtube_id) errors.push('invalid YouTube URL/ID');
    if (!(titleRaw || '').trim()) errors.push('missing title');
    if (!(channelRaw || '').trim()) errors.push('missing channel');
    if (errors.length) errorCount++;
    return {
      topic,
      youtube_id,
      title: (titleRaw || '').trim(),
      channel: (channelRaw || '').trim(),
      duration_label: (durationRaw || '').trim() || null,
      _error: errors.length ? errors.join('; ') : null,
    };
  });
  return { rows, errorCount };
};

const CSV_TEMPLATE = [
  '# Lumera videos bulk import (topic,url,title,channel,duration)',
  '# Topic must be one of: anxiety, depression, fear, grief, hope, peace,',
  '# forgiveness, faith, prayer, purpose, marriage, parenting',
  'topic,url,title,channel,duration',
  'anxiety,https://www.youtube.com/watch?v=dQw4w9WgXcQ,Example Title,Example Channel,3:30',
].join('\n');


/**
 * Super-Admin "Videos" manager: add & remove curated videos, grouped by topic.
 * Uses the existing backend endpoints (GET /api/videos/topics,
 * /api/videos/topic/{slug}, POST /api/videos, DELETE /api/videos/{id}).
 * The backend already gates POST/DELETE to super_admin + moderator.
 */
const VideosAdminPanel = ({ language = 'en' }) => {
  const [topics, setTopics] = useState([]);
  const [activeTopic, setActiveTopic] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // {youtube_id, title}
  // Bulk-import dialog state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCsv, setBulkCsv] = useState('');
  const [bulkParsed, setBulkParsed] = useState(null); // {rows, errorCount}
  const [bulkResult, setBulkResult] = useState(null); // server response
  const [form, setForm] = useState({
    topic: '',
    url: '',
    title: '',
    channel: '',
    duration_label: '',
  });
  const [submitting, setSubmitting] = useState(false);
  // Content Review (moderation console) state
  const [view, setView] = useState('library'); // 'library' | 'review'
  const [modVideos, setModVideos] = useState([]);
  const [modLoading, setModLoading] = useState(false);
  const [modActionId, setModActionId] = useState(null);
  const [reviewFilter, setReviewFilter] = useState('all'); // all | flagged | blocked | unreviewed
  const [recheckingAll, setRecheckingAll] = useState(false);

  const recheckAll = async () => {
    setRecheckingAll(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/videos/admin/recheck-all`, {}, { withCredentials: true });
      toast.success(
        language === 'es'
          ? `Re-revisando ${r.data?.total || ''} videos en segundo plano…`
          : `Re-checking ${r.data?.total || ''} videos in the background…`
      );
      setTimeout(loadModeration, 8000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-check failed');
    } finally {
      setRecheckingAll(false);
    }
  };

  const filteredModVideos = modVideos.filter((v) => {
    const mod = v.moderation || {};
    if (reviewFilter === 'flagged') return (v.flags_count || 0) > 0;
    if (reviewFilter === 'blocked') return mod.allowed === false;
    if (reviewFilter === 'unreviewed') return mod.allowed === null || mod.allowed === undefined;
    return true;
  });

  const loadModeration = useCallback(async () => {
    setModLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/videos/admin/moderation`, { withCredentials: true });
      setModVideos(r.data?.videos || []);
    } catch (e) {
      console.error('load moderation', e);
      toast.error(language === 'es' ? 'Error al cargar revisión' : 'Failed to load content review');
    } finally {
      setModLoading(false);
    }
  }, [language]);

  useEffect(() => { if (view === 'review') loadModeration(); }, [view, loadModeration]);

  const recheckVideo = async (id) => {
    setModActionId(id);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/videos/${encodeURIComponent(id)}/recheck`, {}, { withCredentials: true });
      const v = r.data?.moderation;
      setModVideos((prev) => prev.map((m) => (m.id === id ? { ...m, moderation: v } : m)));
      toast.success((v?.allowed ? '✓ ' : '⚠ ') + (v?.category || '') + ': ' + (v?.reason || ''));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-check failed');
    } finally {
      setModActionId(null);
    }
  };

  const overrideVideo = async (id, allowed) => {
    setModActionId(id);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/videos/${encodeURIComponent(id)}/override`,
        { allowed },
        { withCredentials: true }
      );
      const v = r.data?.moderation;
      setModVideos((prev) => prev.map((m) => (m.id === id ? { ...m, moderation: v } : m)));
      toast.success(allowed
        ? (language === 'es' ? 'Aprobado manualmente' : 'Manually approved')
        : (language === 'es' ? 'Bloqueado (oculto para usuarios)' : 'Blocked (hidden from users)'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Override failed');
    } finally {
      setModActionId(null);
    }
  };

  const loadTopics = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/videos/topics`);
      setTopics(r.data?.topics || []);
      if (!activeTopic && r.data?.topics?.length) {
        setActiveTopic(r.data.topics[0].slug);
      }
    } catch (e) {
      console.error('admin load topics', e);
    } finally {
      setLoading(false);
    }
  }, [activeTopic]);

  const loadVideos = useCallback(async (slug) => {
    if (!slug) return;
    setLoadingVideos(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/videos/topic/${slug}`, {
        params: { limit: 50 },
      });
      setVideos(r.data?.videos || []);
    } catch (e) {
      console.error('admin load topic', e);
      setVideos([]);
    } finally {
      setLoadingVideos(false);
    }
  }, []);

  useEffect(() => { loadTopics(); }, [loadTopics]);
  useEffect(() => { loadVideos(activeTopic); }, [activeTopic, loadVideos]);

  const openAddDialog = () => {
    setForm({
      topic: activeTopic || (topics[0]?.slug ?? ''),
      url: '',
      title: '',
      channel: '',
      duration_label: '',
    });
    setAddOpen(true);
  };

  const submitAdd = async () => {
    const id = extractYouTubeId(form.url);
    if (!id) {
      toast.error(
        language === 'es'
          ? 'URL de YouTube no válida'
          : 'Invalid YouTube URL or ID'
      );
      return;
    }
    if (!form.topic || !form.title.trim() || !form.channel.trim()) {
      toast.error(
        language === 'es'
          ? 'Tema, título y canal son obligatorios'
          : 'Topic, title, and channel are required'
      );
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/videos`,
        {
          topic: form.topic,
          youtube_id: id,
          title: form.title.trim(),
          channel: form.channel.trim(),
          duration_label: form.duration_label.trim() || null,
        },
        { withCredentials: true }
      );
      toast.success(
        language === 'es' ? 'Video añadido' : 'Video added'
      );
      setAddOpen(false);
      await loadTopics();
      if (activeTopic === form.topic) await loadVideos(form.topic);
      else setActiveTopic(form.topic);
    } catch (err) {
      console.error('admin add video', err);
      toast.error(
        err?.response?.data?.detail ||
          (language === 'es' ? 'Error al añadir video' : 'Failed to add video')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitDelete = async () => {
    if (!confirmDelete) return;
    setSubmitting(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/videos/${encodeURIComponent(confirmDelete.youtube_id)}`,
        { withCredentials: true }
      );
      toast.success(language === 'es' ? 'Video eliminado' : 'Video removed');
      setConfirmDelete(null);
      await loadTopics();
      await loadVideos(activeTopic);
    } catch (err) {
      console.error('admin delete video', err);
      toast.error(
        err?.response?.data?.detail ||
          (language === 'es' ? 'Error al eliminar' : 'Failed to delete')
      );
    } finally {
      setSubmitting(false);
    }
  };

  // --- Bulk-import helpers ---
  const openBulk = () => {
    setBulkCsv('');
    setBulkParsed(null);
    setBulkResult(null);
    setBulkOpen(true);
  };

  const handleCsvChange = (text) => {
    setBulkCsv(text);
    setBulkResult(null);
    setBulkParsed(text.trim() ? parseCsv(text) : null);
  };

  const handleCsvFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => handleCsvChange(String(e.target?.result || ''));
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lumera-videos-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const submitBulk = async () => {
    if (!bulkParsed || bulkParsed.rows.length === 0) return;
    const validItems = bulkParsed.rows
      .filter((r) => !r._error)
      .map(({ _error, ...rest }) => rest);
    if (!validItems.length) {
      toast.error(
        language === 'es'
          ? 'Todas las filas tienen errores'
          : 'All rows have errors'
      );
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/videos/bulk`,
        { items: validItems },
        { withCredentials: true }
      );
      setBulkResult(r.data);
      const { added = 0, updated = 0, failed = 0 } = r.data || {};
      toast.success(
        language === 'es'
          ? `${added} añadidos, ${updated} actualizados, ${failed} fallidos`
          : `${added} added, ${updated} updated, ${failed} failed`
      );
      await loadTopics();
      await loadVideos(activeTopic);
    } catch (err) {
      console.error('bulk import', err);
      toast.error(
        err?.response?.data?.detail ||
          (language === 'es' ? 'Error al importar' : 'Import failed')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="videos-admin-panel">
      {/* Topic strip + Add button */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Film className="w-5 h-5 text-primary" />
            <CardTitle className="text-base sm:text-lg">
              {language === 'es' ? 'Biblioteca de videos' : 'Video library'}
            </CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-lg border border-border overflow-hidden mr-1">
              <button
                onClick={() => setView('library')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${view === 'library' ? 'bg-primary text-primary-foreground' : 'bg-transparent hover:bg-muted'}`}
                data-testid="video-admin-view-library"
              >
                {language === 'es' ? 'Biblioteca' : 'Library'}
              </button>
              <button
                onClick={() => setView('review')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors inline-flex items-center gap-1 ${view === 'review' ? 'bg-primary text-primary-foreground' : 'bg-transparent hover:bg-muted'}`}
                data-testid="video-admin-view-review"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                {language === 'es' ? 'Revisión' : 'Content Review'}
              </button>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={openBulk}
              data-testid="video-admin-bulk-btn"
            >
              <Upload className="w-3.5 h-3.5 mr-1" />
              {language === 'es' ? 'Importar CSV' : 'Bulk CSV'}
            </Button>
            <Button size="sm" onClick={openAddDialog} data-testid="video-admin-add-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />
              {language === 'es' ? 'Añadir video' : 'Add video'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {loading && (
            <div className="py-6 text-center text-muted-foreground text-sm">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
              {language === 'es' ? 'Cargando…' : 'Loading…'}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {topics.map((t) => {
              const active = t.slug === activeTopic;
              return (
                <button
                  key={t.slug}
                  onClick={() => setActiveTopic(t.slug)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                    active
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted/50 hover:bg-muted border-border'
                  }`}
                  data-testid={`video-admin-topic-${t.slug}`}
                >
                  {t.label}
                  <span
                    className={`ml-1.5 text-[10px] ${
                      active ? 'opacity-80' : 'opacity-60'
                    }`}
                  >
                    {t.count}
                  </span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Video table */}
      {view === 'library' && (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base capitalize">
            {activeTopic
              ? `${language === 'es' ? 'Videos en' : 'Videos in'} ${activeTopic}`
              : '—'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loadingVideos && (
            <div className="py-10 text-center text-muted-foreground text-sm">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
              {language === 'es' ? 'Cargando videos…' : 'Loading videos…'}
            </div>
          )}
          {!loadingVideos && videos.length === 0 && (
            <p className="py-10 text-center text-muted-foreground text-sm">
              {language === 'es'
                ? 'Sin videos. Usa "Añadir video".'
                : 'No videos. Use "Add video" to curate.'}
            </p>
          )}
          {!loadingVideos && videos.length > 0 && (
            <ul className="divide-y divide-border">
              {videos.map((v) => (
                <li
                  key={v.id}
                  className="p-3 flex items-center gap-3"
                  data-testid={`video-admin-row-${v.id}`}
                >
                  <img
                    src={v.thumbnail_url}
                    alt=""
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    className="w-20 aspect-video object-cover rounded-md shrink-0 bg-muted"
                  />
                  <div className="flex-1 min-w-0 space-y-1">
                    <p className="text-sm font-medium line-clamp-2">{v.title}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">
                        {v.channel}
                      </Badge>
                      {v.duration_label && (
                        <Badge variant="outline" className="text-[10px]">
                          {v.duration_label}
                        </Badge>
                      )}
                      <a
                        href={v.watch_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] text-muted-foreground hover:text-primary inline-flex items-center gap-0.5"
                      >
                        {v.id} <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setConfirmDelete({ youtube_id: v.id, title: v.title })
                    }
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    data-testid={`video-admin-delete-${v.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      )}

      {/* Content Review (moderation console) */}
      {view === 'review' && (
      <Card data-testid="video-admin-review-panel">
        <CardHeader className="pb-3">
          <div className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-primary" />
              {language === 'es' ? 'Revisión de contenido' : 'Content Review'}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={recheckAll} disabled={recheckingAll} data-testid="video-review-recheck-all">
                {recheckingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                {language === 'es' ? 'Re-revisar todo' : 'Re-check all'}
              </Button>
              <Button size="sm" variant="ghost" onClick={loadModeration} disabled={modLoading} data-testid="video-review-refresh">
                {modLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {[
              ['all', language === 'es' ? 'Todos' : 'All'],
              ['flagged', language === 'es' ? 'Reportados' : 'Flagged'],
              ['blocked', language === 'es' ? 'Bloqueados' : 'Blocked'],
              ['unreviewed', language === 'es' ? 'Sin revisar' : 'Unreviewed'],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setReviewFilter(key)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${reviewFilter === key ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/40 hover:bg-muted border-border'}`}
                data-testid={`video-review-filter-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {modLoading && (
            <div className="py-10 text-center text-muted-foreground text-sm">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
              {language === 'es' ? 'Cargando…' : 'Loading…'}
            </div>
          )}
          {!modLoading && modVideos.length === 0 && (
            <p className="py-10 text-center text-muted-foreground text-sm">
              {language === 'es' ? 'Sin videos.' : 'No videos.'}
            </p>
          )}
          {!modLoading && modVideos.length > 0 && filteredModVideos.length === 0 && (
            <p className="py-10 text-center text-muted-foreground text-sm">
              {language === 'es' ? 'Nada en este filtro.' : 'Nothing matches this filter.'}
            </p>
          )}
          {!modLoading && filteredModVideos.length > 0 && (
            <ul className="divide-y divide-border">
              {filteredModVideos.map((v) => {
                const mod = v.moderation || {};
                const allowed = mod.allowed;
                const badgeCls = allowed === false
                  ? 'bg-red-500/15 text-red-500 border-red-500/30'
                  : allowed === true
                  ? 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30'
                  : 'bg-amber-500/15 text-amber-500 border-amber-500/30';
                return (
                  <li key={v.id} className="p-3 flex items-start gap-3" data-testid={`video-review-row-${v.id}`}>
                    <img
                      src={v.thumbnail_url} alt="" loading="lazy" referrerPolicy="no-referrer"
                      className="w-20 aspect-video object-cover rounded-md shrink-0 bg-muted"
                    />
                    <div className="flex-1 min-w-0 space-y-1">
                      <p className="text-sm font-medium line-clamp-2">{v.title}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px] capitalize">{v.topic}</Badge>
                        <Badge variant="outline" className="text-[10px]">{v.channel}</Badge>
                        <Badge variant="outline" className={`text-[10px] ${badgeCls}`}>
                          {allowed === false ? (
                            <><Ban className="w-2.5 h-2.5 mr-0.5 inline" />{mod.category || 'blocked'}</>
                          ) : allowed === true ? (
                            <><ShieldCheck className="w-2.5 h-2.5 mr-0.5 inline" />{mod.category || 'allowed'}</>
                          ) : (
                            <><ShieldAlert className="w-2.5 h-2.5 mr-0.5 inline" />{mod.category || 'unreviewed'}</>
                          )}
                        </Badge>
                        {(v.flags_count || 0) > 0 && (
                          <Badge variant="outline" className="text-[10px] bg-orange-500/15 text-orange-500 border-orange-500/30">
                            <Flag className="w-2.5 h-2.5 mr-0.5 inline" />
                            {v.flags_count} {language === 'es' ? 'reporte(s)' : 'flag(s)'}
                          </Badge>
                        )}
                      </div>
                      {mod.reason && (
                        <p className="text-[11px] text-muted-foreground italic line-clamp-2">{mod.reason}</p>
                      )}
                      {(v.flags && v.flags.length > 0) && (
                        <p className="text-[11px] text-orange-500/90 italic line-clamp-2">
                          “{v.flags[v.flags.length - 1].reason}”
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button
                        variant="outline" size="sm" className="h-7 text-[11px]"
                        onClick={() => recheckVideo(v.id)} disabled={modActionId === v.id}
                        data-testid={`video-review-recheck-${v.id}`}
                      >
                        {modActionId === v.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                        {language === 'es' ? 'Re-revisar' : 'Re-check'}
                      </Button>
                      {allowed !== true && (
                        <Button
                          variant="outline" size="sm"
                          className="h-7 text-[11px] text-emerald-600 border-emerald-500/40 hover:bg-emerald-500/10"
                          onClick={() => overrideVideo(v.id, true)} disabled={modActionId === v.id}
                          data-testid={`video-review-approve-${v.id}`}
                        >
                          <Check className="w-3 h-3 mr-1" />{language === 'es' ? 'Aprobar' : 'Approve'}
                        </Button>
                      )}
                      {allowed !== false && (
                        <Button
                          variant="outline" size="sm"
                          className="h-7 text-[11px] text-red-600 border-red-500/40 hover:bg-red-500/10"
                          onClick={() => overrideVideo(v.id, false)} disabled={modActionId === v.id}
                          data-testid={`video-review-block-${v.id}`}
                        >
                          <Ban className="w-3 h-3 mr-1" />{language === 'es' ? 'Bloquear' : 'Block'}
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
      )}

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="video-admin-add-dialog">
          <DialogHeader>
            <DialogTitle>
              {language === 'es' ? 'Añadir video' : 'Add video'}
            </DialogTitle>
            <DialogDescription>
              {language === 'es'
                ? 'Curar un video de YouTube en una categoría.'
                : 'Curate a YouTube video into a category.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{language === 'es' ? 'Tema' : 'Topic'}</Label>
              <Select
                value={form.topic}
                onValueChange={(v) => setForm((s) => ({ ...s, topic: v }))}
              >
                <SelectTrigger data-testid="video-admin-form-topic">
                  <SelectValue placeholder={language === 'es' ? 'Elige un tema' : 'Pick a topic'} />
                </SelectTrigger>
                <SelectContent>
                  {topics.map((t) => (
                    <SelectItem key={t.slug} value={t.slug}>
                      {t.label} ({t.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>
                {language === 'es' ? 'URL o ID de YouTube' : 'YouTube URL or ID'}
              </Label>
              <Input
                value={form.url}
                onChange={(e) => setForm((s) => ({ ...s, url: e.target.value }))}
                placeholder="https://www.youtube.com/watch?v=..."
                data-testid="video-admin-form-url"
              />
              {form.url && (
                <p className="text-[10px] text-muted-foreground">
                  {extractYouTubeId(form.url)
                    ? `✓ ID: ${extractYouTubeId(form.url)}`
                    : '✗ ' + (language === 'es' ? 'URL no válida' : 'Invalid URL')}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>{language === 'es' ? 'Título' : 'Title'}</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                data-testid="video-admin-form-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{language === 'es' ? 'Canal' : 'Channel'}</Label>
                <Input
                  value={form.channel}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, channel: e.target.value }))
                  }
                  data-testid="video-admin-form-channel"
                />
              </div>
              <div className="space-y-1.5">
                <Label>
                  {language === 'es' ? 'Duración (opcional)' : 'Duration (optional)'}
                </Label>
                <Input
                  value={form.duration_label}
                  placeholder="3:48"
                  onChange={(e) =>
                    setForm((s) => ({ ...s, duration_label: e.target.value }))
                  }
                  data-testid="video-admin-form-duration"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setAddOpen(false)}
              disabled={submitting}
            >
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              onClick={submitAdd}
              disabled={submitting}
              data-testid="video-admin-form-submit"
            >
              {submitting ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <Plus className="w-3.5 h-3.5 mr-1" />
              )}
              {language === 'es' ? 'Añadir' : 'Add video'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <DialogContent className="max-w-md" data-testid="video-admin-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              {language === 'es' ? 'Eliminar video' : 'Delete video'}
            </DialogTitle>
            <DialogDescription>
              {language === 'es'
                ? 'Esta acción no se puede deshacer.'
                : "This can't be undone."}
            </DialogDescription>
          </DialogHeader>
          {confirmDelete && (
            <p className="text-sm text-muted-foreground">
              “{confirmDelete.title}”
            </p>
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(null)}
              disabled={submitting}
            >
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              variant="destructive"
              onClick={submitDelete}
              disabled={submitting}
              data-testid="video-admin-delete-confirm"
            >
              {submitting ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <Trash2 className="w-3.5 h-3.5 mr-1" />
              )}
              {language === 'es' ? 'Eliminar' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk CSV import dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent
          className="max-w-2xl max-h-[85vh] overflow-y-auto"
          data-testid="video-admin-bulk-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" />
              {language === 'es' ? 'Importar videos (CSV)' : 'Bulk import videos (CSV)'}
            </DialogTitle>
            <DialogDescription>
              {language === 'es'
                ? 'Formato por fila: topic,url,title,channel,duration. La cabecera es opcional; las líneas que empiezan con # se ignoran.'
                : 'Format per row: topic,url,title,channel,duration. Header row optional; lines starting with # are ignored.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Template + file-upload controls */}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={downloadTemplate}
                data-testid="video-admin-bulk-template"
              >
                <Download className="w-3.5 h-3.5 mr-1" />
                {language === 'es' ? 'Plantilla' : 'Template'}
              </Button>
              <label className="inline-flex items-center gap-1 text-xs text-muted-foreground cursor-pointer">
                <FileText className="w-3.5 h-3.5" />
                <span className="underline">
                  {language === 'es' ? 'Subir archivo…' : 'Upload file…'}
                </span>
                <input
                  type="file"
                  accept=".csv,text/csv,text/tab-separated-values,.tsv"
                  className="hidden"
                  onChange={(e) => handleCsvFile(e.target.files?.[0])}
                  data-testid="video-admin-bulk-file"
                />
              </label>
            </div>

            <Textarea
              value={bulkCsv}
              onChange={(e) => handleCsvChange(e.target.value)}
              rows={8}
              className="font-mono text-xs"
              placeholder="anxiety,https://youtube.com/watch?v=...,Example Title,Channel,3:30"
              data-testid="video-admin-bulk-textarea"
            />

            {/* Preview table */}
            {bulkParsed && bulkParsed.rows.length > 0 && (
              <div
                className="border rounded-md divide-y max-h-56 overflow-y-auto text-xs"
                data-testid="video-admin-bulk-preview"
              >
                {bulkParsed.rows.slice(0, 100).map((r, i) => (
                  <div
                    key={i}
                    className={`p-2 flex items-start gap-2 ${
                      r._error ? 'bg-destructive/10' : 'bg-background'
                    }`}
                  >
                    <span
                      className={`shrink-0 w-5 h-5 rounded-full text-center text-[10px] leading-5 ${
                        r._error
                          ? 'bg-destructive/30 text-destructive'
                          : 'bg-emerald-500/20 text-emerald-400'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="truncate">
                        <Badge variant="outline" className="text-[10px] mr-1">
                          {r.topic || '—'}
                        </Badge>
                        <span className="font-medium">{r.title || '(no title)'}</span>
                        {r.channel && (
                          <span className="text-muted-foreground"> — {r.channel}</span>
                        )}
                      </p>
                      {r.youtube_id && (
                        <p className="text-muted-foreground font-mono">
                          {r.youtube_id}
                        </p>
                      )}
                      {r._error && (
                        <p className="text-destructive">{r._error}</p>
                      )}
                    </div>
                  </div>
                ))}
                {bulkParsed.rows.length > 100 && (
                  <p className="p-2 text-center text-muted-foreground text-[10px]">
                    … +{bulkParsed.rows.length - 100} more
                  </p>
                )}
              </div>
            )}

            {bulkParsed && (
              <p className="text-xs text-muted-foreground">
                {bulkParsed.rows.length}{' '}
                {language === 'es' ? 'filas totales' : 'total rows'}
                {bulkParsed.errorCount > 0 &&
                  ` · ${bulkParsed.errorCount} ${
                    language === 'es' ? 'con errores' : 'with errors (skipped)'
                  }`}
              </p>
            )}

            {/* Import results */}
            {bulkResult && (
              <div
                className="p-3 rounded-md bg-muted/40 text-xs space-y-1"
                data-testid="video-admin-bulk-result"
              >
                <p className="font-medium">
                  {language === 'es' ? 'Resultados' : 'Results'}: +
                  {bulkResult.added} {language === 'es' ? 'nuevos' : 'new'}, ~
                  {bulkResult.updated}{' '}
                  {language === 'es' ? 'actualizados' : 'updated'}, ✗
                  {bulkResult.failed}{' '}
                  {language === 'es' ? 'fallidos' : 'failed'}
                </p>
                {bulkResult.results &&
                  bulkResult.results
                    .filter((r) => r.status === 'error')
                    .slice(0, 5)
                    .map((r) => (
                      <p key={r.row} className="text-destructive">
                        row {r.row}: {r.error}
                      </p>
                    ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setBulkOpen(false)}
              disabled={submitting}
            >
              {language === 'es' ? 'Cerrar' : 'Close'}
            </Button>
            <Button
              onClick={submitBulk}
              disabled={
                submitting ||
                !bulkParsed ||
                bulkParsed.rows.length === 0 ||
                bulkParsed.rows.every((r) => r._error)
              }
              data-testid="video-admin-bulk-submit"
            >
              {submitting ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <Upload className="w-3.5 h-3.5 mr-1" />
              )}
              {language === 'es'
                ? `Importar ${
                    bulkParsed
                      ? bulkParsed.rows.length - bulkParsed.errorCount
                      : 0
                  }`
                : `Import ${
                    bulkParsed
                      ? bulkParsed.rows.length - bulkParsed.errorCount
                      : 0
                  }`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VideosAdminPanel;
